import React, { useState } from 'react';
import ManageMembership from './pages/Manage_membership.jsx';
import { SecurityProvider } from './members/security/Security_store'; // ✅ Security only for members

// =====================================================================
// 1. POLICY COMPONENTS (LEGAL TOPICS)
//    These are the components for each legal topic requested.
// =====================================================================

const PolicyWrapper = ({ title, children }) => (
    <div className="policy-block box">
        <h3 className="text-h3 policy-title">{title}</h3>
        {children}
    </div>
);

const PrivacyPolicy = () => (
    <PolicyWrapper title="Corporate Privacy Policy">
        <p className="text-p">We collect only necessary data, processed under strict compliance with global standards. Your data is never sold, leased, or shared without explicit, written consent. We uphold the right to be forgotten and provide tools for all users to manage their personal information.</p>
        <p className="text-p">Last Updated: October 2025.</p>
    </PolicyWrapper>
);

const EnvironmentalPolicy = () => (
    <PolicyWrapper title="Global Environmental Impact Policy">
        <p className="text-p">Committed to achieving net-zero carbon emissions across all operations and supply chains by 2030. This involves mandatory sustainable sourcing, minimizing waste generation, and investing 1% of annual profit into ecological restoration projects globally.</p>
    </PolicyWrapper>
);

const AntiSlaveryPolicy = () => (
    <PolicyWrapper title="Anti-Slavery & Human Rights Policy">
        <p className="text-p">We enforce a zero-tolerance approach to modern slavery, forced labor, and child labor across our entire supply chain (Tier 1 to Tier 3). Audits are mandatory, unannounced, and conducted by independent third parties. Any breach results in immediate termination of contracts and reporting to relevant authorities.</p>
    </PolicyWrapper>
);

// --- Policy Detail View (The page wrapper for showing policies) ---
const PolicyDetailView = ({ policyType, setSelectedPolicy }) => {
    let PolicyComponent;
    let viewTitle = "";

    switch (policyType) {
        case 'privacy':
            PolicyComponent = PrivacyPolicy;
            viewTitle = "Privacy Policy";
            break;
        case 'environment':
            PolicyComponent = EnvironmentalPolicy;
            viewTitle = "Environmental Policy";
            break;
        case 'slavery':
            PolicyComponent = AntiSlaveryPolicy;
            viewTitle = "Anti-Slavery & Human Rights Policy";
            break;
        default:
            return null;
    }

    return (
        <div className="policy-detail-container">
            <h1 className="text-h1" style={{ marginBottom: '30px' }}>{viewTitle}</h1>
            <button
                onClick={() => setSelectedPolicy(null)}
                className="btn btn-secondary btn-sm"
                style={{ marginBottom: '40px' }}
            >
                &larr; Back to Home
            </button>
            <PolicyComponent />
        </div>
    );
};


// =====================================================================
// 2. STRUCTURAL COMPONENTS (HEADER & FOOTER)
// =====================================================================

// --- Header Component (Fixed/Sticky to Top) ---
const CorporateHeader = ({ setView }) => {
    return (
        <header className="corporate-header">
            <div className="header-content-inner">
                <h2 className="logo-title text-h2" onClick={() => setView('home')}>
                    Cradle to Grave
                </h2>
                <nav>
                    <button
                        onClick={() => setView('styleguide')}
                        className="btn btn-outline-primary btn-sm"
                    >
                        Style Guide
                    </button>
                    <button 
                        onClick={() => setView('manage_membership')} // ✅ correct key for switch()
                        className="btn btn-primary btn-sm" 
                        style={{ marginLeft: '10px' }}
                    >
                        Login / Register
                    </button>
                </nav>
            </div>
        </header>
    );
};


// --- Footer Component (Contains Legal Links) ---
const CorporateFooter = ({ setSelectedPolicy }) => {
    return (
        <footer className="corporate-footer">
            <div className="footer-links-container">
                <div className="footer-section">
                    <h5 className="text-h4">Company</h5>
                    <ul className="footer-list">
                        <li>
                            <button className="footer-link btn btn-link p-0" onClick={() => {}}>
                                About Us
                            </button>
                        </li>
                        {/* ✅ replaced anchors with button-styled links to avoid anchor-is-valid */}
                        <li>
                            <button className="footer-link btn btn-link p-0" onClick={() => {}}>
                                Careers
                            </button>
                        </li>
                        <li>
                            <button className="footer-link btn btn-link p-0" onClick={() => {}}>
                                Contact
                            </button>
                        </li>
                    </ul>
                </div>

                <div className="footer-section legal-section">
                    <h5 className="text-h4">Legal Notices & Policies</h5>
                    <ul className="footer-list">
                        {/* Policy links trigger state change to show component */}
                        <li>
                            <button className="footer-link" onClick={() => setSelectedPolicy('privacy')}>
                                Privacy Policy
                            </button>
                        </li>
                        <li>
                            <button className="footer-link" onClick={() => setSelectedPolicy('environment')}>
                                Environmental Policy
                            </button>
                        </li>
                        <li>
                            <button className="footer-link" onClick={() => setSelectedPolicy('slavery')}>
                                Anti-Slavery Policy
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="copyright text-p">
                &copy; {new Date().getFullYear()} Cradle to Grave Corp. All rights reserved.
            </div>
        </footer>
    );
};


// =====================================================================
// 3. PAGE COMPONENTS
// =====================================================================

// --- Home Page Component (The Main Page Content) ---
const HomePage = ({ setSelectedPolicy }) => {
    return (
        <div className="home-page-wrapper">
            <h1 className="text-h1">The Future of Perpetual Services</h1>

            <section className="box content-section" style={{ maxWidth: '800px', margin: '20px auto 40px auto' }}>
                <h3 className="text-h2">Clarity and Integrity in Every Service</h3>
                <p className="text-p">
                    Our commitment is built on transparent operations and ethical standards. We believe that trust is the only true non-renewable resource, and we dedicate ourselves to preserving it in every transaction. Below, you can find quick access to our core legal and ethical frameworks.
                </p>
                <div className="box box-accent" style={{ marginTop: '20px' }}>
                    <p className="text-p">Explore our service portfolio for tailored solutions.</p>
                </div>
            </section>

            <section className="legal-quick-links-area">
                 <h3 className="text-h3">Core Ethical Policies</h3>
                 <div className="quick-links-grid">
                     <button onClick={() => setSelectedPolicy('privacy')} className="btn btn-outline-primary btn-lg">Privacy Policy</button>
                     <button onClick={() => setSelectedPolicy('environment')} className="btn btn-outline-primary btn-lg">Environment Policy</button>
                     <button onClick={() => setSelectedPolicy('slavery')} className="btn btn-outline-primary btn-lg">Anti-Slavery Policy</button>
                 </div>
            </section>
            
            <section className="content-section" style={{ maxWidth: '800px', margin: '40px auto' }}>
                <h3 className="text-h2">Our Comprehensive Solutions</h3>
                <p className="text-p">
                    We offer comprehensive end-to-end solutions, integrating cutting-edge technology with time-honored practices. Our services are designed for long-term reliability and adaptability, ensuring that your needs are met today and in the future.
                </p>
            </section>
        </div>
    );
};

// --- Style Guide Page Component (Minimalist version for demonstration) ---
const StyleGuidePage = ({ setView }) => {
    return (
        <div className="styleguide-page">
            <header className="styleguide-header">
                <h1 className="text-h1">Corporate Style Guide</h1>
            </header>

            <button
                onClick={() => setView('home')}
                className="btn btn-secondary" 
                style={{ marginBottom: '20px' }}
            >
                &larr; Back to Home
            </button>

            {/* Content trimmed for brevity. */}
            <section className="style-section typography-section">
                <h2 className="style-section-title text-h2">1. Typography</h2>
                <h1 className="text-h1">H1 Heading</h1>
                <p className="text-p">Standard text paragraph.</p>
            </section>
        </div>
    );
};


// =====================================================================
// 4. MAIN APPLICATION COMPONENT (The Router and Layout Wrapper)
// =====================================================================

const App = () => {
    // State to manage the current main view ('home' or 'styleguide' or 'manage_membership')
    const [view, setView] = useState('home');
    // State to manage if a policy is being shown (null, 'privacy', 'environment', 'slavery')
    const [selectedPolicy, setSelectedPolicy] = useState(null);

    let mainContent;

    if (selectedPolicy) {
        // If a policy is selected (via Footer or Homepage link), show the detail view
        mainContent = <PolicyDetailView policyType={selectedPolicy} setSelectedPolicy={setSelectedPolicy} />;
    } else {
        // Otherwise, render the main view based on the 'view' state
        switch (view) {
            case 'styleguide':
                // The StyleGuide Page uses its own content wrapping
                mainContent = <StyleGuidePage setView={setView} />;
                break;
            case 'manage_membership':
                // 🔐 Secure ONLY the members area by wrapping its subtree
                mainContent = (
                    <SecurityProvider>
                        <ManageMembership setView={setView} />
                    </SecurityProvider>
                );
                break;
            case 'home':
            default:
                // HomePage content
                mainContent = <HomePage setSelectedPolicy={setSelectedPolicy} />;
                break;
        }
    }

    return (
        <div className="app-main">
            {/* Header (Glued to Top) */}
            <CorporateHeader setView={setView} />
            
            {/* Main Content Area (Handles scrolling and grows) */}
            <main className="app-content-area">
                <div className="app-content-inner">
                    {mainContent}
                </div>
            </main>

            {/* Footer (Always at the bottom) */}
            <CorporateFooter setSelectedPolicy={setSelectedPolicy} />
        </div>
    );
};

export default App;